import { ApiError } from '@api/api-client';
import {
  type CategoryResponse,
  type CategorySortField,
  getCategories,
  type SortDirection,
} from '@api/categories-api';
import {
  createEditionCategory,
  type EditionCategoryDetailResponse,
  unlinkEditionCategory,
} from '@api/edition-categories-api';
import Breadcrumbs from '@components/breadcrumbs';
import ConfirmationModal from '@components/confirmation-modal';
import TableFilters from '@components/table-filters';
import { useAuth } from '@hooks/use-auth';
import { useCategories } from '@hooks/use-categories';
import { useEdition } from '@hooks/use-edition';
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ChevronLeft,
  ChevronRight,
  Eye,
  ListChecks,
  Search,
  Unlink,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { sileo } from 'sileo';

const PICKER_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

function getCategoryName(item: EditionCategoryDetailResponse): string {
  return item.category.details[0]?.name ?? item.category.name;
}

function getCategoryDisplayName(cat: CategoryResponse, countryId?: string): string {
  if (countryId) {
    const localDetail = cat.details.find((d) => d.countryId === countryId);
    if (localDetail) return localDetail.name;
  }
  return cat.details[0]?.name ?? cat.name;
}

type ClinicFilter = 'all' | 'clinic' | 'doctor';

interface CategoryPickerProps {
  categories: CategoryResponse[];
  selectedIds: Set<string>;
  searchQuery: string;
  isLoading: boolean;
  isModal: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  sortBy: CategorySortField;
  sortDir: SortDirection;
  clinicFilter: ClinicFilter;
  countryId?: string;
  onSearchChange: (query: string) => void;
  onPageChange: (page: number) => void;
  onSortToggle: (field: CategorySortField) => void;
  onClinicFilterChange: (filter: ClinicFilter) => void;
  onToggle: (id: string) => void;
  onAssign: () => void;
  onClose: () => void;
}

function CategoryPicker({
  categories,
  selectedIds,
  searchQuery,
  isLoading,
  isModal,
  currentPage,
  totalPages,
  totalItems,
  sortBy,
  sortDir,
  clinicFilter,
  countryId,
  onSearchChange,
  onPageChange,
  onSortToggle,
  onClinicFilterChange,
  onToggle,
  onAssign,
  onClose,
}: CategoryPickerProps): React.JSX.Element {
  const SortIcon = sortDir === 'asc' ? ArrowDownAZ : ArrowUpAZ;

  const picker = (
    <div className="category-picker">
      <div className="category-picker__header">
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-tertiary)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            className="category-picker__search"
            style={{ paddingLeft: 36 }}
            placeholder="Search categories by name or ID..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
          <button
            type="button"
            className={`btn btn-sm ${clinicFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onClinicFilterChange('all')}
            title="Show all categories"
          >
            All
          </button>
          <button
            type="button"
            className={`btn btn-sm ${clinicFilter === 'doctor' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onClinicFilterChange('doctor')}
            title="Show doctor categories only"
          >
            Doctor
          </button>
          <button
            type="button"
            className={`btn btn-sm ${clinicFilter === 'clinic' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onClinicFilterChange('clinic')}
            title="Show clinic categories only"
          >
            Clinic
          </button>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
          <button
            type="button"
            className={`btn btn-sm ${sortBy === 'detailName' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onSortToggle('detailName')}
            title="Sort by country name"
          >
            {sortBy === 'detailName' ? <SortIcon size={14} /> : <ArrowDownAZ size={14} />}
            Name
          </button>
          <button
            type="button"
            className={`btn btn-sm ${sortBy === 'externalId' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onSortToggle('externalId')}
            title="Sort by ID"
          >
            {sortBy === 'externalId' ? <SortIcon size={14} /> : <ArrowDownAZ size={14} />}
            ID
          </button>
        </div>

        <span className="category-picker__count">
          {selectedIds.size} selected of {totalItems}
        </span>
        {isModal && (
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            aria-label="Close picker"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        )}
      </div>

      <div className="category-picker__list">
        {isLoading && <p className="category-picker__empty">Loading categories...</p>}

        {!isLoading && categories.length === 0 && (
          <p className="category-picker__empty">
            {searchQuery ? 'No categories match your search' : 'No categories available'}
          </p>
        )}

        {categories.map((cat) => {
          const isSelected = selectedIds.has(cat.id);
          return (
            <label
              key={cat.id}
              className={`category-picker__item ${isSelected ? 'category-picker__item--selected' : ''}`}
            >
              <input
                type="checkbox"
                className="category-picker__checkbox"
                checked={isSelected}
                onChange={() => onToggle(cat.id)}
              />
              <span className="category-picker__name">
                {getCategoryDisplayName(cat, countryId)}
              </span>
              <span className="category-picker__badges">
                {cat.isClinic && <span className="badge badge-clinic">Clinic</span>}
                {cat.isGlobal && <span className="badge badge-global">Global</span>}
              </span>
            </label>
          );
        })}
      </div>

      <div className="category-picker__footer">
        <span className="category-picker__count">
          {selectedIds.size} categor{selectedIds.size !== 1 ? 'ies' : 'y'} selected
        </span>

        {totalPages > 1 && (
          <div className="pagination pagination--compact">
            <span className="pagination__info">
              Page {currentPage} of {totalPages}
            </span>
            <div className="pagination__controls">
              <button
                type="button"
                className="pagination__btn"
                disabled={currentPage === 1}
                aria-label="Previous page"
                onClick={() => onPageChange(currentPage - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className="pagination__btn"
                disabled={currentPage === totalPages}
                aria-label="Next page"
                onClick={() => onPageChange(currentPage + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary"
          disabled={selectedIds.size === 0}
          onClick={onAssign}
        >
          <ListChecks size={16} />
          Assign categories
        </button>
      </div>
    </div>
  );

  if (!isModal) return picker;

  return (
    <div
      className="category-picker-overlay"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClose();
      }}
      role="presentation"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {picker}
      </div>
    </div>
  );
}

export default function Categories(): React.JSX.Element {
  const { isAdmin } = useAuth();
  const { editionId } = useParams<{ editionId: string }>();
  const { edition } = useEdition(editionId);
  const { categories, isLoading, refetch } = useCategories(editionId);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerCategories, setPickerCategories] = useState<CategoryResponse[]>([]);
  const [isLoadingPicker, setIsLoadingPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerTotalPages, setPickerTotalPages] = useState(1);
  const [pickerTotalItems, setPickerTotalItems] = useState(0);
  const [pickerSortBy, setPickerSortBy] = useState<CategorySortField>('detailName');
  const [pickerSortDir, setPickerSortDir] = useState<SortDirection>('desc');
  const [clinicFilter, setClinicFilter] = useState<ClinicFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [unlinkingCategory, setUnlinkingCategory] = useState<EditionCategoryDetailResponse | null>(
    null,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const [tableSearch, setTableSearch] = useState('');
  const [tableSortField, setTableSortField] = useState<string>('name');
  const [tableSortDir, setTableSortDir] = useState<SortDirection>('asc');

  const hasNoCategories = !isLoading && categories.length === 0;
  const showPicker = isPickerOpen || hasNoCategories;

  const filteredAndSortedCategories = (() => {
    const lowerSearch = tableSearch.toLowerCase().trim();
    const filtered = lowerSearch
      ? categories.filter((item) => getCategoryName(item).toLowerCase().includes(lowerSearch))
      : categories;

    return [...filtered].sort((a, b) => {
      const direction = tableSortDir === 'asc' ? 1 : -1;
      if (tableSortField === 'votes') {
        return (a.voteCount - b.voteCount) * direction;
      }
      return getCategoryName(a).localeCompare(getCategoryName(b)) * direction;
    });
  })();

  const handleTableSortToggle = (field: string): void => {
    if (tableSortField === field) {
      setTableSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setTableSortField(field);
      setTableSortDir('asc');
    }
  };

  const assignedCategoryIds = new Set(categories.map((c) => c.category.id));

  const editionCountryId = edition?.countryId;

  const fetchPickerCategories = useCallback(
    async (
      page: number,
      search: string,
      sortBy: CategorySortField,
      sortDir: SortDirection,
      countryId?: string,
      isClinic?: boolean,
    ): Promise<void> => {
      setIsLoadingPicker(true);
      try {
        const response = await getCategories({
          page,
          pageSize: PICKER_PAGE_SIZE,
          search: search || undefined,
          sortBy,
          sortDir,
          countryId,
          isClinic,
        });
        setPickerCategories(response.data);
        setPickerTotalPages(response.meta.totalPages);
        setPickerTotalItems(response.meta.totalItems);
      } catch {
        sileo.error({ title: 'Failed to load categories' });
      } finally {
        setIsLoadingPicker(false);
      }
    },
    [],
  );

  const isClinicParam = clinicFilter === 'all' ? undefined : clinicFilter === 'clinic';

  useEffect(() => {
    if (showPicker) {
      void fetchPickerCategories(
        pickerPage,
        debouncedSearch,
        pickerSortBy,
        pickerSortDir,
        editionCountryId,
        isClinicParam,
      );
    }
  }, [
    showPicker,
    pickerPage,
    debouncedSearch,
    pickerSortBy,
    pickerSortDir,
    editionCountryId,
    isClinicParam,
    fetchPickerCategories,
  ]);

  const handlePickerSortToggle = (field: CategorySortField): void => {
    if (pickerSortBy === field) {
      setPickerSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setPickerSortBy(field);
      setPickerSortDir('asc');
    }
    setPickerPage(1);
  };

  const handleClinicFilterChange = (filter: ClinicFilter): void => {
    setClinicFilter(filter);
    setPickerPage(1);
  };

  useEffect(() => {
    if (showPicker) {
      setSelectedIds(new Set(assignedCategoryIds));
    }
    // Only sync when the picker opens or categories change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPicker, categories]);

  const handleSearchChange = (query: string): void => {
    setSearchQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(query);
      setPickerPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };

  const toggleSelection = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAssign = async (): Promise<void> => {
    if (!editionId) return;

    const toAdd = [...selectedIds].filter((id) => !assignedCategoryIds.has(id));
    const toRemove = [...assignedCategoryIds].filter((id) => !selectedIds.has(id));

    try {
      for (const categoryId of toAdd) {
        try {
          await createEditionCategory({ editionId, categoryId });
        } catch (error: unknown) {
          if (error instanceof ApiError && error.status === 409) continue;
          throw error;
        }
      }

      for (const categoryId of toRemove) {
        await unlinkEditionCategory(editionId, categoryId);
      }

      sileo.success({ title: 'Categories updated successfully' });
      closePicker();
      refetch();
    } catch {
      sileo.error({ title: 'Failed to update categories' });
    }
  };

  const openPicker = (): void => {
    setSearchQuery('');
    setDebouncedSearch('');
    setPickerPage(1);
    setClinicFilter('all');
    setIsPickerOpen(true);
  };

  const closePicker = (): void => {
    setIsPickerOpen(false);
    setSearchQuery('');
    setDebouncedSearch('');
    setPickerPage(1);
    setClinicFilter('all');
  };

  const handleUnlink = async (item: EditionCategoryDetailResponse): Promise<void> => {
    if (!editionId) return;

    try {
      await unlinkEditionCategory(editionId, item.category.id);
      sileo.success({ title: 'Category unlinked successfully' });
      setUnlinkingCategory(null);
      refetch();
    } catch {
      sileo.error({ title: 'Failed to unlink category' });
    }
  };

  const editionLabel = edition ? `${edition.country} ${edition.year}`.trim() : 'Edition';

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: editionLabel }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Categories</h1>
          <p className="page-header__subtitle">
            {editionLabel} &mdash; {categories.length} registered categories
          </p>
        </div>
        {isAdmin && (
          <div className="page-header__actions">
            {categories.length > 0 && (
              <button type="button" className="btn btn-primary" onClick={openPicker}>
                <ListChecks size={16} />
                Assign categories
              </button>
            )}
          </div>
        )}
      </div>

      {showPicker && (
        <CategoryPicker
          categories={pickerCategories}
          selectedIds={selectedIds}
          searchQuery={searchQuery}
          isLoading={isLoadingPicker}
          isModal={isPickerOpen}
          currentPage={pickerPage}
          totalPages={pickerTotalPages}
          totalItems={pickerTotalItems}
          sortBy={pickerSortBy}
          sortDir={pickerSortDir}
          clinicFilter={clinicFilter}
          countryId={edition?.countryId}
          onSearchChange={handleSearchChange}
          onPageChange={setPickerPage}
          onSortToggle={handlePickerSortToggle}
          onClinicFilterChange={handleClinicFilterChange}
          onToggle={toggleSelection}
          onAssign={() => void handleAssign()}
          onClose={closePicker}
        />
      )}

      {!showPicker && (
        <>
          <TableFilters
            compact
            rows={[
              {
                items: [
                  {
                    type: 'search',
                    id: 'category-search',
                    label: 'Search',
                    placeholder: 'Search by category name...',
                    value: tableSearch,
                    onChange: setTableSearch,
                  },
                  {
                    type: 'sort',
                    activeField: tableSortField,
                    direction: tableSortDir,
                    buttons: [
                      { field: 'name', label: 'Name' },
                      { field: 'votes', label: 'Votes', variant: 'numeric' },
                    ],
                    onToggle: handleTableSortToggle,
                  },
                ],
              },
            ]}
          />
          <div className="table-container">
            {filteredAndSortedCategories.length === 0 ? (
              <p className="category-picker__empty">
                {tableSearch ? 'No categories match your search' : 'No categories available'}
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Nominees</th>
                    <th>Votes</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedCategories.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="category-name-cell">
                          <strong>{getCategoryName(item)}</strong>
                          {item.category.isClinic && (
                            <span className="badge badge-clinic">Clinic</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="stat-chip">
                          <span className="stat-chip__value">{item.nomineeCount}</span>
                        </span>
                      </td>
                      <td>
                        <span className="stat-chip">
                          <span className="stat-chip__value">{item.voteCount}</span>
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/editions/${editionId}/categories/${item.category.id}/nominees`}
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="View nominees"
                            aria-label="View nominees"
                          >
                            <Eye size={16} />
                          </Link>
                          {isAdmin && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon btn-sm"
                              data-tooltip="Unlink category"
                              aria-label="Unlink category"
                              onClick={() => setUnlinkingCategory(item)}
                            >
                              <Unlink size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <ConfirmationModal
        isOpen={unlinkingCategory !== null}
        onClose={() => setUnlinkingCategory(null)}
        onConfirm={() => {
          if (unlinkingCategory) return handleUnlink(unlinkingCategory);
        }}
        title="Unlink category"
        message={`Are you sure you want to unlink "${unlinkingCategory ? getCategoryName(unlinkingCategory) : ''}" from this edition? This action cannot be undone.`}
        confirmLabel="Unlink"
        variant="danger"
      />
    </>
  );
}
